import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import {
  get_automation_static_directory_datasets_url,
  get_process_dataset_url,
} from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { STATIC_DATASETS_KEY, STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import { notifications } from '@mantine/notifications';
import {
  ManualDatasetCreateResponse,
  ManualDirectoryDatasetCreateRequest,
} from '@/hooks/api/datasets/static/types.ts';
import { RelativeFile } from '@/types/common.ts';
import {
  getSignedUploadConfig,
  relativeFileToUploadMetadata,
} from '@/hooks/api/datasets/uploadMetadata.ts';

type props = {
  name: string;
  files: RelativeFile[];
};

const useCreateStaticDirectoryDataset = (
  options: UseMutationOptions<ManualDatasetCreateResponse, AxiosError, props>,
): UseMutationResult<ManualDatasetCreateResponse, AxiosError, props> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, files }: props) => {
      const filteredFiles = files.filter(
        (relativeFile) => !relativeFile.file.name.endsWith('.DS_Store'),
      );

      const postData: ManualDirectoryDatasetCreateRequest = {
        name: name,
        files: filteredFiles.map(relativeFileToUploadMetadata),
      };

      const response = await apiClient.post<ManualDatasetCreateResponse>(
        get_automation_static_directory_datasets_url(automationId),
        postData,
      );

      const datasetId = response.data.dataset.id;

      const unsupportedFiles = response.data.unsupported_files;

      if (unsupportedFiles.length > 0) {
        const fileTypes = unsupportedFiles.map((file) => file.split('.').pop());
        const uniqueFileTypes = Array.from(new Set(fileTypes));
        notifications.show({
          title: `Skipping ${unsupportedFiles.length} unsupported files`,
          message: `Skipped unsupported file types: ${uniqueFileTypes.join(', ')}`,
          color: 'yellow',
        });
      }

      const urls = response.data.urls;
      for (const relativeFile of filteredFiles) {
        const uploadMetadata = relativeFileToUploadMetadata(relativeFile);
        if (unsupportedFiles.includes(uploadMetadata.path)) {
          continue;
        }
        const url = urls[uploadMetadata.path];
        if (!url) {
          throw new Error(`Missing upload URL for ${uploadMetadata.path}`);
        }
        await axios.put(url, relativeFile.file, getSignedUploadConfig(uploadMetadata.content_type));
      }

      await apiClient.put(get_process_dataset_url(datasetId), {});

      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });

      return response.data;
    },
    ...options,
  });
};

export default useCreateStaticDirectoryDataset;
